/**
 * HealthDashboard Component
 *
 * Persona Engine health dashboard with 4 metric cards at top (total generations,
 * failover rate, avg confidence, engine distribution) and Recharts
 * visualizations below: confidence histogram, failover rate over time,
 * and engine usage breakdown pie chart.
 */

import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Activity, AlertTriangle, BarChart3, PieChart as PieChartIcon, Cpu, TrendingUp } from 'lucide-react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import type { PersonaEngineLogEntry, EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

// ─── Constants ───────────────────────────────────────────────────────────────

const ENGINE_COLORS: Record<string, string> = {
  ipc: '#6366f1',    // indigo
  cat: '#0ea5e9',    // sky
  rft: '#f59e0b',    // amber
  aic: '#10b981',    // emerald
  mi: '#8b5cf6',     // violet
  scarf: '#ef4444',  // red
}

const CONFIDENCE_BINS = [
  { label: '0.0-0.2', min: 0, max: 0.2 },
  { label: '0.2-0.4', min: 0.2, max: 0.4 },
  { label: '0.4-0.6', min: 0.4, max: 0.6 },
  { label: '0.6-0.8', min: 0.6, max: 0.8 },
  { label: '0.8-1.0', min: 0.8, max: 1.0 },
]

// ─── Types ───────────────────────────────────────────────────────────────────

interface HealthDashboardProps {
  entries?: PersonaEngineLogEntry[]
  isLoading?: boolean
  error?: Error | null
}

// ─── Metric card sub-component ───────────────────────────────────────────────

interface MetricCardProps {
  label: string
  value: string | number
  icon: React.ElementType
  description: string
  color: string
}

function MetricCard({ label, value, icon: Icon, description, color }: MetricCardProps) {
  const colorMap: Record<string, { bg: string; iconColor: string }> = {
    mindmetal: { bg: 'bg-personic-50', iconColor: 'text-personic-600' },
    amber: { bg: 'bg-amber-50', iconColor: 'text-amber-600' },
    emerald: { bg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    indigo: { bg: 'bg-indigo-50', iconColor: 'text-indigo-600' },
  }
  const c = colorMap[color] || colorMap.mindmetal

  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}
      className="p-5 rounded-xl border border-gray-200 bg-white relative overflow-hidden"
    >
      <div className="absolute -right-3 -top-3 w-20 h-20 bg-personic-50/50 rounded-full" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${c.iconColor}`} />
          </div>
        </div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <motion.p
          key={String(value)}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-gray-900 mt-1"
        >
          {value}
        </motion.p>
        <p className="text-xs text-gray-400 mt-1.5">{description}</p>
      </div>
    </motion.div>
  )
}

// ─── Chart card wrapper ──────────────────────────────────────────────────────

function ChartCard({ title, icon: Icon, children }: {
  title: string
  icon: React.ElementType
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-personic-600" />
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="h-[260px]">{children}</div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function HealthDashboard({ entries = [], isLoading = false, error = null }: HealthDashboardProps) {
  // Compute metrics
  const metrics = useMemo(() => {
    if (entries.length === 0) {
      return {
        totalGenerations: 0,
        failoverRate: 0,
        avgConfidence: 0,
        topEngine: 'N/A',
      }
    }

    const failoverCount = entries.filter(
      (e) => e.failover_chain && e.failover_chain.length > 1
    ).length

    const avgConf =
      entries.reduce((sum, e) => sum + e.engine_confidence_score, 0) / entries.length

    // Engine distribution
    const engineCounts: Record<string, number> = {}
    for (const entry of entries) {
      engineCounts[entry.engine_name] = (engineCounts[entry.engine_name] || 0) + 1
    }
    const topEngine = Object.entries(engineCounts).sort(
      (a, b) => b[1] - a[1]
    )[0]?.[0] ?? 'N/A'

    return {
      totalGenerations: entries.length,
      failoverRate: (failoverCount / entries.length) * 100,
      avgConfidence: avgConf,
      topEngine: ENGINE_SHORT_LABELS[topEngine as EngineType] ?? topEngine.toUpperCase(),
    }
  }, [entries])

  // Confidence histogram data
  const histogramData = useMemo(() => {
    return CONFIDENCE_BINS.map((bin) => ({
      range: bin.label,
      count: entries.filter(
        (e) => e.engine_confidence_score >= bin.min && e.engine_confidence_score < (bin.max === 1.0 ? 1.01 : bin.max)
      ).length,
    }))
  }, [entries])

  // Failover rate over time (group by date)
  const failoverTimeSeries = useMemo(() => {
    if (entries.length === 0) return []

    const byDate: Record<string, { total: number; failovers: number }> = {}
    for (const entry of entries) {
      const date = entry.generation_timestamp.split('T')[0]
      if (!byDate[date]) byDate[date] = { total: 0, failovers: 0 }
      byDate[date].total++
      if (entry.failover_chain && entry.failover_chain.length > 1) {
        byDate[date].failovers++
      }
    }

    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => ({
        date,
        failover_rate: data.total > 0 ? (data.failovers / data.total) * 100 : 0,
        total: data.total,
      }))
  }, [entries])

  // Engine usage pie data
  const pieData = useMemo(() => {
    const engineCounts: Record<string, number> = {}
    for (const entry of entries) {
      engineCounts[entry.engine_name] = (engineCounts[entry.engine_name] || 0) + 1
    }
    return Object.entries(engineCounts).map(([engine, count]) => ({
      name: ENGINE_SHORT_LABELS[engine as EngineType] ?? engine.toUpperCase(),
      value: count,
      color: ENGINE_COLORS[engine] || '#94a3b8',
    }))
  }, [entries])

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-[140px] rounded-xl border border-gray-200 bg-gray-50 animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-[320px] rounded-xl border border-gray-200 bg-gray-50 animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-600">Failed to load generation data</h3>
        <p className="text-sm text-gray-400 mt-1 max-w-sm">
          {error.message || 'An unexpected error occurred while fetching Persona Engine generation logs.'}
        </p>
      </motion.div>
    )
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <Activity className="w-12 h-12 text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-600">No generation data</h3>
        <p className="text-sm text-gray-400 mt-1 max-w-sm">
          Persona Engine generation logs will appear here once persona renders have been run.
        </p>
      </motion.div>
    )
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <MetricCard
          label="Total Generations"
          value={metrics.totalGenerations.toLocaleString()}
          icon={Cpu}
          description="All-time Persona Engine render runs"
          color="mindmetal"
        />
        <MetricCard
          label="Failover Rate"
          value={`${metrics.failoverRate.toFixed(1)}%`}
          icon={AlertTriangle}
          description="Percent of runs requiring failover"
          color="amber"
        />
        <MetricCard
          label="Avg Confidence"
          value={metrics.avgConfidence.toFixed(3)}
          icon={TrendingUp}
          description="Mean engine confidence score"
          color="emerald"
        />
        <MetricCard
          label="Top Engine"
          value={metrics.topEngine}
          icon={Activity}
          description="Most frequently selected engine"
          color="indigo"
        />
      </motion.div>

      {/* Charts */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.3 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        {/* Confidence histogram */}
        <ChartCard title="Confidence Distribution" icon={BarChart3}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={histogramData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="range"
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="count"
                name="Generations"
                fill="#7c3aed"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Failover rate over time */}
        <ChartCard title="Failover Rate Over Time" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={failoverTimeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
                tickFormatter={(v: number) => `${v.toFixed(0)}%`}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  fontSize: 12,
                }}
                labelFormatter={formatDate}
                formatter={(value: number) => [`${value.toFixed(1)}%`, 'Failover Rate']}
              />
              <Line
                type="monotone"
                dataKey="failover_rate"
                name="Failover Rate"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Engine usage pie chart */}
        <ChartCard title="Engine Usage Breakdown" icon={PieChartIcon}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={95}
                dataKey="value"
                nameKey="name"
                paddingAngle={2}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                labelLine={{ strokeWidth: 1 }}
              >
                {pieData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  fontSize: 12,
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value: string) => (
                  <span className="text-gray-700">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </motion.div>
    </div>
  )
}
